import React, { useState, createContext, useContext, useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'

// Layouts
import PublicLayout from './layouts/PublicLayout'
import DashboardLayout from './layouts/DashboardLayout'

// Public Pages
import LandingPage from './pages/public/LandingPage'
import AboutPage from './pages/public/AboutPage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'

// Onboarding
import BrandOnboarding from './pages/onboarding/BrandOnboarding'
import CreatorOnboarding from './pages/onboarding/CreatorOnboarding'

// Brand Portal
import BrandDashboard from './pages/brand/BrandDashboard'
import BrandCampaigns from './pages/brand/BrandCampaigns'
import BrandBriefBuilder from './pages/brand/BrandBriefBuilder'
import BrandCreatorDiscovery from './pages/brand/BrandCreatorDiscovery'
import BrandCampaignDetail from './pages/brand/BrandCampaignDetail'

// Creator Portal
import CreatorDashboard from './pages/creator/CreatorDashboard'
import CreatorOpportunities from './pages/creator/CreatorOpportunities'
import CreatorProfile from './pages/creator/CreatorProfile'
import CreatorDeliverables from './pages/creator/CreatorDeliverables'

// Admin Console
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminMatching from './pages/admin/AdminMatching'
import AdminCompliance from './pages/admin/AdminCompliance'
import AdminAnalytics from './pages/admin/AdminAnalytics'

// Auth Context
export const AuthContext = createContext(null)

export const useAuth = () => useContext(AuthContext)

// Mock user data for demo
const DEMO_USERS = {
  brand: {
    id: 'demo-brand-1',
    email: 'marketing@medtech.com',
    firstName: 'Sarah',
    lastName: 'Johnson',
    name: 'Sarah Johnson',
    userType: 'brand',
    role: 'brand_marketing',
    company: 'MedTech Innovations',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
    emailVerified: true,
    onboardingComplete: true,
    profile: {
      companyName: 'MedTech Innovations',
      industry: 'medtech',
      jobTitle: 'VP of Marketing',
    }
  },
  creator: {
    id: 'demo-creator-1',
    email: 'dr.grunch@email.com',
    firstName: 'Betsy',
    lastName: 'Grunch',
    name: 'Dr. Betsy Grunch',
    userType: 'creator',
    role: 'creator',
    title: 'Neurosurgeon & Health Educator',
    avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100&h=100&fit=crop',
    verified: true,
    tier: 'flagship',
    emailVerified: true,
    onboardingComplete: true,
    profile: {
      professionalType: 'physician',
      specialties: ['Neurosurgery', 'Neurology'],
    }
  },
  admin: {
    id: 'demo-admin-1',
    email: 'admin@healthfluence.com',
    firstName: 'Admin',
    lastName: 'User',
    name: 'Healthfluence Admin',
    userType: 'admin',
    role: 'hf_admin',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
    emailVerified: true,
    onboardingComplete: true,
  }
}

// Local storage key for persisting user
const USER_STORAGE_KEY = 'healthfluence_user'

function App() {
  const [user, setUser] = useState(() => {
    // Try to restore user from localStorage
    const saved = localStorage.getItem(USER_STORAGE_KEY)
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        return null
      }
    }
    return null
  })
  const [isLoading, setIsLoading] = useState(false)

  // Persist user to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
    } else {
      localStorage.removeItem(USER_STORAGE_KEY)
    }
  }, [user])

  const login = async (email, password, userType) => {
    setIsLoading(true)

    try {
      // First try to authenticate with the API
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, userType }),
      })

      if (response.ok) {
        const data = await response.json()
        const userData = {
          ...data.user,
          name: `${data.user.firstName} ${data.user.lastName}`,
          role: data.user.userType === 'brand' ? 'brand_marketing' :
                data.user.userType === 'creator' ? 'creator' : 'hf_admin',
        }
        setUser(userData)
        return userData
      }
    } catch (err) {
      console.log('API login failed, falling back to demo mode')
    }

    // Fall back to demo users
    await new Promise(resolve => setTimeout(resolve, 500))
    const demoUser = DEMO_USERS[userType]
    setUser(demoUser)
    setIsLoading(false)
    return demoUser
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem(USER_STORAGE_KEY)
  }

  const setRegisteredUser = (userData) => {
    const enhancedUser = {
      ...userData,
      name: `${userData.firstName} ${userData.lastName}`,
      role: userData.userType === 'brand' ? 'brand_marketing' :
            userData.userType === 'creator' ? 'creator' : 'hf_admin',
    }
    setUser(enhancedUser)
  }

  const updateUserProfile = (userData) => {
    const enhancedUser = {
      ...userData,
      name: `${userData.firstName} ${userData.lastName}`,
      role: userData.userType === 'brand' ? 'brand_marketing' :
            userData.userType === 'creator' ? 'creator' : 'hf_admin',
    }
    setUser(enhancedUser)
  }

  const getDashboardPath = () => {
    if (!user) return '/login'

    // Check if user needs onboarding
    if (!user.onboardingComplete && user.userType !== 'admin') {
      return `/onboarding/${user.userType}`
    }

    switch (user.role) {
      case 'brand_admin':
      case 'brand_marketing':
        return '/brand'
      case 'creator':
        return '/creator'
      case 'hf_admin':
      case 'hf_campaign_manager':
        return '/admin'
      default:
        return '/login'
    }
  }

  // Protected Route wrapper
  const ProtectedRoute = ({ children, allowedTypes }) => {
    if (!user) {
      return <Navigate to="/login" replace />
    }

    if (allowedTypes && !allowedTypes.includes(user.userType) && !allowedTypes.includes(user.role)) {
      return <Navigate to={getDashboardPath()} replace />
    }

    return children
  }

  // Onboarding Route wrapper - requires user but incomplete onboarding
  const OnboardingRoute = ({ children, requiredType }) => {
    if (!user) {
      return <Navigate to="/login" replace />
    }

    if (user.onboardingComplete) {
      return <Navigate to={getDashboardPath()} replace />
    }

    if (requiredType && user.userType !== requiredType) {
      return <Navigate to={`/onboarding/${user.userType}`} replace />
    }

    return children
  }

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      isLoading,
      getDashboardPath,
      setRegisteredUser,
      updateUserProfile
    }}>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Route>

        {/* Onboarding Routes */}
        <Route path="/onboarding/brand" element={
          <OnboardingRoute requiredType="brand">
            <BrandOnboarding />
          </OnboardingRoute>
        } />
        <Route path="/onboarding/creator" element={
          <OnboardingRoute requiredType="creator">
            <CreatorOnboarding />
          </OnboardingRoute>
        } />

        {/* Brand Portal */}
        <Route path="/brand" element={
          <ProtectedRoute allowedTypes={['brand', 'brand_marketing', 'brand_admin']}>
            <DashboardLayout portalType="brand" />
          </ProtectedRoute>
        }>
          <Route index element={<BrandDashboard />} />
          <Route path="campaigns" element={<BrandCampaigns />} />
          <Route path="campaigns/new" element={<BrandBriefBuilder />} />
          <Route path="campaigns/:id" element={<BrandCampaignDetail />} />
          <Route path="creators" element={<BrandCreatorDiscovery />} />
        </Route>

        {/* Creator Portal */}
        <Route path="/creator" element={
          <ProtectedRoute allowedTypes={['creator']}>
            <DashboardLayout portalType="creator" />
          </ProtectedRoute>
        }>
          <Route index element={<CreatorDashboard />} />
          <Route path="opportunities" element={<CreatorOpportunities />} />
          <Route path="deliverables" element={<CreatorDeliverables />} />
          <Route path="profile" element={<CreatorProfile />} />
        </Route>

        {/* Admin Console */}
        <Route path="/admin" element={
          <ProtectedRoute allowedTypes={['admin', 'hf_admin', 'hf_campaign_manager']}>
            <DashboardLayout portalType="admin" />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="matching" element={<AdminMatching />} />
          <Route path="compliance" element={<AdminCompliance />} />
          <Route path="analytics" element={<AdminAnalytics />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthContext.Provider>
  )
}

export default App
